import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { FirebaseError } from 'firebase/app';
import { Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';

import vizitProBanner from '@/assets/brand/banner.png';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  auth,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
  updatePassword,
} from '@/lib/firebase';

import { getFirebaseMessage } from '../login/helpers';
import { signUpSchema, type SignUpValues } from '../login/schema';

// Unauthenticated, no app shell — lands an invited employee here after they
// click the Firebase passwordless sign-in link an owner generated for them
// (see `src/routes/employees/invite-link-panel.tsx`). The link carries
// `mode=signIn&oobCode=...&apiKey=...` in the query string; Firebase does not
// send them through its own hosted page first (the backend mints the link
// with `HandleCodeInApp: true`), so this page is the entire landing
// experience. The app otherwise only supports email+password/Google login,
// so completing the passwordless sign-in isn't enough on its own — we also
// have the employee set a password here so they can log in normally after.
export function AcceptInvite() {
  const navigate = useNavigate();
  const isValidLink = isSignInWithEmailLink(auth, window.location.href);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignUpValues>({ resolver: zodResolver(signUpSchema) });

  async function onSubmit({ email, password }: SignUpValues) {
    try {
      // `auth.currentUser` may already be set if this page remounted after a
      // prior successful sign-in (AuthProvider briefly blanks the whole app
      // while it syncs the new session) — the oobCode is single-use, so
      // re-calling signInWithEmailLink on that remount would fail. But the
      // same browser could also already be signed into an unrelated account
      // (e.g. the owner testing their own invite link), so only reuse
      // `auth.currentUser` when its email actually matches what was typed
      // here — otherwise sign that other account out first.
      let firebaseUser = auth.currentUser;
      const isSameUser =
        firebaseUser?.email?.toLowerCase() === email.trim().toLowerCase();
      if (!firebaseUser || !isSameUser) {
        if (firebaseUser) await signOut(auth);
        const credential = await signInWithEmailLink(
          auth,
          email,
          window.location.href,
        );
        firebaseUser = credential.user;
      }
      await updatePassword(firebaseUser, password);
      toast.success('Your account is set up. Signing you in…');
      navigate('/schedule', { replace: true });
    } catch (error) {
      const code = error instanceof FirebaseError ? error.code : 'auth/unknown';
      toast.error(getFirebaseMessage(code));
    }
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-4">
      <div className="flex flex-col items-center gap-2">
        <div className="rounded-xl bg-white px-5 py-3">
          <img src={vizitProBanner} alt="VizitPro" className="h-16 w-auto sm:h-20" />
        </div>
        <span className="text-muted-foreground text-sm">
          Run your service business, simplified.
        </span>
      </div>

      {!isValidLink && !auth.currentUser ? (
        <Card className="w-full max-w-sm gap-4 py-8" data-testid="accept-invite-invalid">
          <CardContent>
            <p className="text-center text-sm text-muted-foreground">
              This invite link is invalid or has expired. Ask your employer to send
              you a new one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="w-full max-w-sm gap-4 py-8">
          <CardHeader>
            <CardTitle className="text-center text-xl">
              Activate your account
            </CardTitle>
          </CardHeader>
          <CardContent>
            {auth.currentUser && (
              <p
                className="mb-3 rounded-md bg-muted p-2 text-center text-xs text-muted-foreground"
                data-testid="accept-invite-signed-in-notice"
              >
                You&apos;re currently signed in as{' '}
                <span className="font-medium">{auth.currentUser.email}</span> in
                this browser. Activating this invite will sign that account out.
              </p>
            )}
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-3"
              data-testid="accept-invite-form"
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ai-email">Email</Label>
                <Input
                  id="ai-email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email)}
                  data-testid="email-input"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-destructive text-xs">{errors.email.message}</p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ai-password">Password</Label>
                <Input
                  id="ai-password"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  aria-invalid={Boolean(errors.password)}
                  data-testid="password-input"
                  {...register('password')}
                />
                {errors.password && (
                  <p className="text-destructive text-xs">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ai-confirm">Confirm password</Label>
                <Input
                  id="ai-confirm"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  aria-invalid={Boolean(errors.confirmPassword)}
                  data-testid="confirm-password-input"
                  {...register('confirmPassword')}
                />
                {errors.confirmPassword && (
                  <p className="text-destructive text-xs">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="mt-1 w-full"
                data-loading={isSubmitting}
                data-testid="accept-invite-submit"
              >
                {isSubmitting && <Loader2Icon className="animate-spin" />}
                Activate account
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
